import * as THREE from 'three';
import { getFurniture } from '../../shared/content';
/** Small original models with a common local origin, so every part rotates together. */
export function createFurnitureModel(itemId: string) {
  const item = getFurniture(itemId)!;
  const root = new THREE.Group();
  root.name = itemId;
  const wood = '#a47755',
    dark = '#51483e',
    cream = '#f1e6d1';
  const mesh = (geometry: THREE.BufferGeometry, color: string, x: number, y: number, z: number) => {
    const m = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({ color, roughness: 0.95 }));
    m.position.set(x, y, z);
    root.add(m);
    return m;
  };
  const box = (color: string, x: number, y: number, z: number, w: number, h: number, d: number) =>
    mesh(new THREE.BoxGeometry(w, h, d), color, x, y, z);
  const cylinder = (color: string, x: number, y: number, z: number, r: number, h: number) =>
    mesh(new THREE.CylinderGeometry(r, r, h, 12), color, x, y, z);
  if (item.kind === 'rug') {
    box(item.color, 0, 0.025, 0, 1.7, 0.045, 1.2);
    box(cream, 0, 0.052, 0.48, 1.55, 0.008, 0.035);
    box(cream, 0, 0.052, -0.48, 1.55, 0.008, 0.035);
  } else if (item.kind === 'plant') {
    cylinder('#bb8965', 0, 0.2, 0, 0.22, 0.4);
    cylinder('#556445', 0, 0.55, 0, 0.025, 0.5);
    for (const [x, y, z] of [
      [-0.1, 0.67, 0],
      [0.11, 0.8, 0.08],
      [0, 0.96, -0.08],
    ])
      mesh(new THREE.IcosahedronGeometry(0.22, 0), item.color, x, y, z);
  } else if (item.kind === 'lamp') {
    cylinder(dark, 0, 0.035, 0, 0.23, 0.07);
    cylinder(dark, 0, 0.7, 0, 0.035, 1.4);
    mesh(new THREE.CylinderGeometry(0.22, 0.3, 0.32, 12), item.color, 0, 1.42, 0);
  } else if (item.kind === 'shelf') {
    for (const x of [-0.61, 0.61]) box(item.color, x, 0.9, 0, 0.08, 1.8, 0.56);
    for (let row = 0; row < 4; row++) {
      box(item.color, 0, 0.13 + row * 0.5, 0, 1.3, 0.065, 0.62);
      if (row < 3)
        for (let b = 0; b < 4; b++)
          box(
            ['#bb8261', '#668e91', '#c5aa63', '#8c8496'][b]!,
            -0.4 + b * 0.23,
            0.32 + row * 0.5,
            0.08,
            0.13,
            0.3 + (b % 2) * 0.06,
            0.3,
          );
    }
  } else if (['sofa', 'chair', 'bench'].includes(item.kind)) {
    const w = item.kind === 'sofa' ? 1.8 : 1;
    for (const x of [-w * 0.38, w * 0.38])
      for (const z of [-0.23, 0.23]) box(wood, x, 0.18, z, 0.1, 0.35, 0.1);
    box(item.color, 0, 0.43, 0, w, 0.22, 0.65);
    box(item.color, 0, 0.76, -0.28, w, 0.48, 0.13);
    if (item.kind !== 'bench')
      for (const x of [-w / 2 + 0.08, w / 2 - 0.08]) box(item.color, x, 0.66, 0, 0.14, 0.3, 0.63);
    if (item.kind === 'sofa') box('#e4c8a4', 0.4, 0.71, -0.12, 0.35, 0.35, 0.15);
  } else if (item.kind === 'guitar') {
    const body = mesh(new THREE.SphereGeometry(0.28, 12, 8), item.color, 0, 0.45, 0);
    body.scale.set(1, 1.25, 0.35);
    box(wood, 0, 1.02, 0, 0.09, 0.75, 0.07);
    cylinder(dark, 0, 0.08, 0.15, 0.2, 0.08);
    const hole = cylinder(dark, 0, 0.53, 0.1, 0.075, 0.01);
    hole.rotation.x = Math.PI / 2;
  } else if (item.kind === 'art' || item.kind === 'mirror') {
    box(wood, 0, 0.73, 0, 0.9, 1.35, 0.12);
    box(item.color, 0, 0.74, 0.069, 0.79, 1.22, 0.022);
    if (item.kind === 'art') {
      box(cream, -0.16, 0.7, 0.09, 0.18, 0.5, 0.012);
      box('#d2a561', 0.12, 0.9, 0.09, 0.28, 0.35, 0.012);
    } else box('#d9e5df', -0.17, 0.95, 0.09, 0.025, 0.65, 0.01);
    box(wood, 0, 0.12, 0.15, 0.72, 0.1, 0.42);
  } else if (item.kind === 'pillows') {
    const a = box(item.color, -0.16, 0.17, 0, 0.5, 0.25, 0.47);
    a.rotation.y = 0.25;
    const b = box('#dec69b', 0.2, 0.24, 0.06, 0.45, 0.3, 0.43);
    b.rotation.y = -0.35;
  } else {
    for (const x of [-0.38, 0.38]) for (const z of [-0.22, 0.22]) box(wood, x, 0.3, z, 0.075, 0.6, 0.075);
    if (item.kind === 'table') cylinder(item.color, 0, 0.65, 0, 0.5, 0.09);
    else box(item.color, 0, 0.65, 0, 1, 0.1, 0.68);
    if (['desk', 'record', 'coffee-cart'].includes(item.kind)) {
      box('#d9c9a6', -0.22, 0.73, 0, 0.29, 0.07, 0.28);
      if (itemId === 'coffee-cart') {
        cylinder(cream, 0.2, 0.8, 0.05, 0.09, 0.22);
        box(dark, 0.15, 0.85, -0.18, 0.25, 0.28, 0.2);
      }
      if (itemId === 'record') {
        box(dark, 0.1, 0.76, 0, 0.4, 0.12, 0.35);
        cylinder('#292a2a', 0.1, 0.83, 0, 0.13, 0.015);
      }
    }
  }
  return root;
}
